import type {
  JournalTotal,
  LedgerReportName,
  LedgerReportRange,
  LedgerReportResponse,
  SystemAccountKind,
} from "@pax/api-contract/accounting/v1alpha1";
import { panelMotion } from "@pax/ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { creditsFromRaw } from "@/features/credits/format";
import { Tabs } from "@/features/evals/components";
import { Problem } from "@/features/staff/components";
import { ChartControls } from "./charts/ChartControls";
import { HighlightScope } from "./charts/highlight";
import { PeriodStepper } from "./charts/PeriodStepper";
import { StackedBars } from "./charts/StackedBars";
import { formatChartCredits, formatPeriod, largestFirst } from "./charts/shared";
import { Credits, DataThrough, Note, Table } from "./components";
import {
  accountKindLabels,
  journalKinds,
  journalReportLabel,
  reportRangeLabels,
  reportRangePhrase,
  systemAccountLabel,
} from "./format";
import { Tags } from "./pickers";
import { ledgerReportQuery } from "./queries";

type Report<Name extends LedgerReportName> = Extract<LedgerReportResponse, { report: Name }>;

const ranges = Object.keys(reportRangeLabels) as LedgerReportRange[];

/** What a report shows: its tags, then under the chart controls, its charts and tables. */
interface ReportBody {
  tags?: ReactNode;
  chart: ReactNode;
}

/**
 * The range buttons and the report's view switch, then its tags, the chart controls with the period
 * stepper, when its data runs to, and its charts and tables.
 */
function ReportFrame<Name extends LedgerReportName>({
  name,
  range,
  back,
  onPeriodChange,
  filters,
  children,
}: {
  name: Name;
  range: LedgerReportRange;
  back: number;
  onPeriodChange: (next: { range?: LedgerReportRange; back?: number }) => void;
  filters?: (report: Report<Name>) => ReactNode;
  children: (report: Report<Name>) => ReportBody;
}) {
  const query = useQuery(ledgerReportQuery(name, range, back));
  const report = query.data as Report<Name> | undefined;
  const shown =
    report?.from && report.to && !query.isPlaceholderData
      ? formatPeriod(new Date(report.from), new Date(report.to), report.step, range === "month")
      : null;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Tabs
          compact
          value={range}
          options={ranges.map(value => ({ value, label: reportRangeLabels[value] }))}
          onChange={next => onPeriodChange({ range: next, back: undefined })}
        />
        {report?.to && filters && (
          <div className="-ml-1 flex flex-wrap gap-1">{filters(report)}</div>
        )}
      </div>
      <Problem error={query.error} />
      {!report ? (
        !query.error && <Note>Loading…</Note>
      ) : !report.to ? (
        <Note>No data has been rolled up yet.</Note>
      ) : (
        <ReportContent
          body={children(report)}
          busy={query.isPlaceholderData}
          stepper={
            <PeriodStepper
              back={back}
              label={shown}
              onChange={next => onPeriodChange({ back: next || undefined })}
            />
          }
          through={back === 0 ? report.to : null}
        />
      )}
    </div>
  );
}

function ReportContent({
  body,
  busy,
  stepper,
  through,
}: {
  body: ReportBody;
  busy: boolean;
  stepper: ReactNode;
  through: string | null;
}) {
  return (
    <HighlightScope>
      <div aria-busy={busy} className={`space-y-4 ${panelMotion} ${busy ? "opacity-60" : ""}`}>
        {body.tags}
        <ChartControls controls={stepper}>
          {through && <DataThrough to={through} />}
          {body.chart}
        </ChartControls>
      </div>
    </HighlightScope>
  );
}

const isZero = (raw: string) => BigInt(raw) === 0n;

const sumRaw = (raws: readonly string[]) =>
  raws.reduce((total, raw) => total + BigInt(raw), 0n).toString();

/** Each bucket's value for each picked key, read from the bucket's rows. */
function chartBuckets<Row, Value>(
  buckets: readonly { bucketStart: string; rows: readonly Row[] }[],
  keys: readonly string[],
  key: (row: Row) => string,
  value: (row: Row | undefined) => Value,
) {
  return buckets.map(bucket => {
    const byKey = new Map(bucket.rows.map(row => [key(row), row]));
    return { start: bucket.bucketStart, values: keys.map(picked => value(byKey.get(picked))) };
  });
}

/** Plain amounts, or signed by which way they moved player balances. Off leaves the address clean. */
function DirectionSwitch({
  signed,
  labels,
  onChange,
}: {
  signed: boolean;
  labels: { plain: string; signed: string };
  onChange: (signed: true | undefined) => void;
}) {
  return (
    <Tabs
      compact
      value={signed ? "signed" : "moved"}
      options={[
        { value: "moved", label: labels.plain },
        { value: "signed", label: labels.signed },
      ]}
      onChange={value => onChange(value === "signed" || undefined)}
    />
  );
}

/** `all` in the address: every option, past the usual pick. */
const EVERY_OPTION = "all";

/**
 * The options shown and how to keep a change to them. With none in the address, the `usual` pick;
 * keeping it leaves the address clean.
 */
function usualPick(
  picked: readonly string[] | undefined,
  every: readonly string[],
  usual: readonly string[],
) {
  const same = (left: readonly string[], right: readonly string[]) =>
    left.length === right.length && left.every(code => right.includes(code));
  return {
    usual,
    values: !picked ? usual : picked.includes(EVERY_OPTION) ? every : picked,
    keep: (next: string[]) =>
      same(next, usual) ? undefined : same(next, every) ? [EVERY_OPTION] : next,
  };
}

type SystemAccountFlow = Report<"systemAccountFlows">["totals"][number];

const volume = (flow: SystemAccountFlow) =>
  BigInt(flow.debitTotalRaw) + BigInt(flow.creditTotalRaw);

/** Out here because the React Compiler turns a bigint literal inside a component into `undefined`. */
const totalVolume = (flows: readonly SystemAccountFlow[]) =>
  flows.reduce((sum, flow) => sum + volume(flow), 0n);

/** Credits less debits: what an account took in, negative where it paid out to players. */
const takenInRaw = (flow: SystemAccountFlow | undefined) =>
  (BigInt(flow?.creditTotalRaw ?? "0") - BigInt(flow?.debitTotalRaw ?? "0")).toString();

const PLAYER_BALANCES = { key: "player-balances", label: "Player balances" } as const;

/** What moved into player balances: what every system account in `flows` paid out, signed. */
const playersRaw = (flows: readonly (SystemAccountFlow | undefined)[]) =>
  (-BigInt(sumRaw(flows.map(takenInRaw)))).toString();

const signedChartCredits = (credits: number) =>
  `${credits > 0 ? "+" : ""}${formatChartCredits(credits)}`;

const systemAccountKinds = Object.keys(accountKindLabels) as SystemAccountKind[];

/** Rows under one heading, such as a kind's accounts or a provider's models. */
interface RowGroup<Row> {
  key: string;
  label: string;
  /** Every one of the group's rows is picked. */
  whole: boolean;
  shown: readonly Row[];
}

/**
 * A stack for each group with every row picked, unless it is the only group shown; the other
 * picked rows stack on their own. Largest first.
 */
function groupStacks<Row>(
  groups: readonly RowGroup<Row>[],
  member: (row: Row) => { key: string; label: string },
  size: (rows: readonly Row[]) => bigint,
) {
  return largestFirst(
    groups.flatMap(group =>
      group.whole && groups.length > 1
        ? [
            {
              key: `group:${group.key}`,
              label: group.label,
              members: group.shown.map(row => member(row).key),
              size: size(group.shown),
            },
          ]
        : group.shown.map(row => ({
            ...member(row),
            members: [member(row).key],
            size: size([row]),
          })),
    ),
    stack => stack.size,
  );
}

interface TableRow {
  key: string;
  cells: readonly ReactNode[];
}

/** Each group's heading row, then its rows under it: with `listSingles` always, else past one. */
function groupedRows<Row>(
  groups: readonly RowGroup<Row>[],
  heading: (group: RowGroup<Row>) => TableRow,
  member: (row: Row) => TableRow,
  listSingles = false,
) {
  return groups.flatMap(group => [
    heading(group),
    ...(listSingles || group.shown.length > 1 ? group.shown.map(member) : []),
  ]);
}

const groupHeading = (label: string) => (
  <span key="heading" className="font-medium">
    {label}
  </span>
);

const groupMember = (label: ReactNode) => (
  <span key="member" className="pl-4">
    {label}
  </span>
);

/**
 * The picked system accounts' credits less debits, and player balances if picked: what is left over
 * from every account, so with everything picked each bucket adds up to zero. A kind with all its
 * accounts picked stacks as one, unless it is the only kind shown; other picked accounts stack on
 * their own.
 */
function BalancedFlows({
  report,
  range,
  back,
  moved,
  picked,
}: {
  report: Report<"systemAccountFlows">;
  range: LedgerReportRange;
  back: number;
  moved: readonly SystemAccountFlow[];
  picked: ReadonlySet<string>;
}) {
  const kinds = systemAccountKinds
    .map(kind => {
      const accounts = largestFirst(
        moved.filter(total => total.accountKind === kind),
        volume,
      );
      const shown = accounts.filter(total => picked.has(total.accountCode));
      return {
        key: kind as string,
        label: accountKindLabels[kind],
        whole: shown.length === accounts.length,
        shown,
      };
    })
    .filter(kind => kind.shown.length > 0);
  const showPlayers = picked.has(PLAYER_BALANCES.key);
  const stacks = groupStacks(
    kinds,
    total => ({ key: total.accountCode, label: systemAccountLabel(total.accountCode) }),
    totalVolume,
  );
  const playersTotal = playersRaw(report.totals);
  const netRaw = sumRaw([...report.totals.map(takenInRaw), playersTotal]);
  const sumOf = (flows: readonly SystemAccountFlow[], raw: (flow: SystemAccountFlow) => string) =>
    sumRaw(flows.map(raw));
  const row = (key: string, label: ReactNode, flows: readonly SystemAccountFlow[]) => ({
    key,
    cells: [
      label,
      <Credits key="debit" raw={sumOf(flows, flow => flow.debitTotalRaw)} />,
      <Credits key="credit" raw={sumOf(flows, flow => flow.creditTotalRaw)} />,
      <Credits key="taken" signed raw={sumOf(flows, takenInRaw)} />,
    ],
  });
  const buckets = report.buckets.map(bucket => {
    const byCode = new Map(bucket.rows.map(flow => [flow.accountCode, flow]));
    return {
      start: bucket.bucketStart,
      values: [
        ...(showPlayers ? [creditsFromRaw(playersRaw(bucket.rows))] : []),
        ...stacks.map(stack =>
          creditsFromRaw(sumRaw(stack.members.map(code => takenInRaw(byCode.get(code))))),
        ),
      ],
    };
  });
  return (
    <>
      <StackedBars
        title="System accounts and player balances, adding up to zero"
        series={[...(showPlayers ? [PLAYER_BALANCES] : []), ...stacks]}
        buckets={buckets}
        step={report.step}
        bucketSummary={index =>
          `${signedChartCredits(creditsFromRaw(playersRaw(report.buckets[index]?.rows ?? [])))} into player balances`
        }
        summary={
          <>
            <Credits signed raw={playersTotal} /> into player balances{" "}
            {reportRangePhrase(range, back)}
            {!isZero(netRaw) && (
              <span className="text-destructive">
                {" "}
                · Net <Credits signed raw={netRaw} />, which should be 0
              </span>
            )}
          </>
        }
        format={formatChartCredits}
      />
      <Table
        columns={[
          { label: "Account" },
          { label: "Debits", numeric: true },
          { label: "Credits", numeric: true },
          { label: "Credits less debits", numeric: true },
        ]}
        rows={[
          ...groupedRows(
            kinds,
            kind => row(`kind:${kind.key}`, groupHeading(kind.label), kind.shown),
            total =>
              row(total.accountCode, groupMember(systemAccountLabel(total.accountCode)), [total]),
          ),
          ...(showPlayers
            ? [
                {
                  key: PLAYER_BALANCES.key,
                  cells: [
                    groupHeading(PLAYER_BALANCES.label),
                    "",
                    "",
                    <Credits key="taken" signed raw={playersTotal} />,
                  ],
                },
              ]
            : []),
          ...(isZero(netRaw)
            ? []
            : [
                {
                  key: "net",
                  cells: [
                    <span key="label" className="font-medium text-destructive">
                      Net, which should be 0
                    </span>,
                    "",
                    "",
                    <span key="net" className="text-destructive">
                      <Credits signed raw={netRaw} />
                    </span>,
                  ],
                },
              ]),
        ]}
      />
    </>
  );
}

/**
 * System accounts by flow, or with `signed`, balanced against player balances. The tags are the
 * journals' kind-by-kind tags, and player balances; with none in the address, the granted accounts
 * by flow, and everything balanced.
 */
export function SystemFlows({
  range,
  back,
  accountCodes,
  signed,
  onChange,
}: {
  range: LedgerReportRange;
  back: number;
  accountCodes: readonly string[] | undefined;
  signed: boolean;
  onChange: (next: {
    range?: LedgerReportRange;
    back?: number;
    accountCode?: string[];
    signed?: true | undefined;
  }) => void;
}) {
  return (
    <ReportFrame
      name="systemAccountFlows"
      range={range}
      back={back}
      onPeriodChange={onChange}
      filters={() => (
        <DirectionSwitch
          signed={signed}
          labels={{ plain: "Flow", signed: "Balanced" }}
          onChange={next => onChange({ signed: next })}
        />
      )}
    >
      {report => {
        const moved = report.totals.filter(
          total => !(isZero(total.debitTotalRaw) && isZero(total.creditTotalRaw)),
        );
        if (moved.length === 0) return { chart: <Note>No activity in this range.</Note> };
        const kinds = systemAccountKinds
          .map(kind => ({
            kind,
            accounts: moved.filter(total => total.accountKind === kind),
          }))
          .filter(kind => kind.accounts.length > 0);
        const ordered = kinds.flatMap(kind => kind.accounts);
        const every = [...ordered.map(total => total.accountCode), PLAYER_BALANCES.key];
        const pick = usualPick(
          accountCodes,
          every,
          signed
            ? every
            : ordered
                .filter(total => total.accountKind === "granted")
                .map(total => total.accountCode),
        );
        const picked = new Set(pick.values);
        const rows = largestFirst(
          ordered.filter(total => picked.has(total.accountCode)),
          total => BigInt(total.flowTotalRaw),
        );
        const showKind = new Set(rows.map(total => total.accountKind)).size > 1;
        const showPlayers = picked.has(PLAYER_BALANCES.key);
        const playersTotal = playersRaw(report.totals);
        return {
          tags: (
            <Tags
              label="Accounts"
              options={[
                ...ordered.map(total => ({
                  value: total.accountCode,
                  label: systemAccountLabel(total.accountCode),
                })),
                { value: PLAYER_BALANCES.key, label: PLAYER_BALANCES.label },
              ]}
              sections={kinds.map(kind => ({
                key: kind.kind,
                label: accountKindLabels[kind.kind],
                members: kind.accounts.map(total => total.accountCode),
              }))}
              usual={pick.usual}
              values={pick.values}
              onChange={codes => onChange({ accountCode: pick.keep(codes) })}
            />
          ),
          chart: signed ? (
            <BalancedFlows
              report={report}
              range={range}
              back={back}
              moved={moved}
              picked={picked}
            />
          ) : rows.length === 0 && !showPlayers ? (
            <Note>None of the picked accounts moved in this range.</Note>
          ) : (
            <>
              <StackedBars
                title="Flow per account"
                series={[
                  ...rows.map(total => ({
                    key: total.accountCode,
                    label: systemAccountLabel(total.accountCode),
                  })),
                  ...(showPlayers ? [PLAYER_BALANCES] : []),
                ]}
                buckets={report.buckets.map(bucket => {
                  const byCode = new Map(bucket.rows.map(flow => [flow.accountCode, flow]));
                  return {
                    start: bucket.bucketStart,
                    values: [
                      ...rows.map(total =>
                        creditsFromRaw(byCode.get(total.accountCode)?.flowTotalRaw ?? "0"),
                      ),
                      ...(showPlayers ? [creditsFromRaw(playersRaw(bucket.rows))] : []),
                    ],
                  };
                })}
                step={report.step}
                summary={
                  rows.length === 0 ? (
                    <>
                      <Credits signed raw={playersTotal} /> into player balances{" "}
                      {reportRangePhrase(range, back)}
                    </>
                  ) : (
                    <>
                      <Credits raw={sumRaw(rows.map(total => total.flowTotalRaw))} /> through{" "}
                      {rows.length === 1 ? "1 account" : `${rows.length} accounts`}{" "}
                      {reportRangePhrase(range, back)}
                    </>
                  )
                }
                format={formatChartCredits}
              />
              <Table
                columns={[
                  { label: "Account" },
                  ...(showKind ? [{ label: "Kind" }] : []),
                  { label: "Debits", numeric: true },
                  { label: "Credits", numeric: true },
                  { label: "Flow", numeric: true },
                ]}
                rows={[
                  ...rows.map(total => ({
                    key: total.accountCode,
                    cells: [
                      systemAccountLabel(total.accountCode),
                      ...(showKind ? [accountKindLabels[total.accountKind]] : []),
                      <Credits key="debit" raw={total.debitTotalRaw} />,
                      <Credits key="credit" raw={total.creditTotalRaw} />,
                      <Credits key="flow" raw={total.flowTotalRaw} />,
                    ],
                  })),
                  {
                    key: "total",
                    cells: [
                      <span key="label" className="font-medium">
                        Total
                      </span>,
                      ...(showKind ? [""] : []),
                      <Credits key="debit" raw={sumRaw(rows.map(total => total.debitTotalRaw))} />,
                      <Credits
                        key="credit"
                        raw={sumRaw(rows.map(total => total.creditTotalRaw))}
                      />,
                      <Credits key="flow" raw={sumRaw(rows.map(total => total.flowTotalRaw))} />,
                    ],
                  },
                  ...(showPlayers
                    ? [
                        {
                          key: PLAYER_BALANCES.key,
                          cells: [
                            groupHeading(PLAYER_BALANCES.label),
                            ...(showKind ? [""] : []),
                            "",
                            "",
                            <Credits key="flow" signed raw={playersTotal} />,
                          ],
                        },
                      ]
                    : []),
                ]}
              />
            </>
          ),
        };
      }}
    </ReportFrame>
  );
}

/** Every journal kind with the journals that moved in it, then any others as "Other". */
function movedJournalKinds(moved: readonly JournalTotal[]) {
  const codes = moved.map(total => total.journalCode);
  const known = new Set(journalKinds.flatMap(kind => kind.journals));
  return [
    ...journalKinds,
    {
      key: "other",
      label: "Other",
      direction: 0 as const,
      journals: codes.filter(code => !known.has(code)),
    },
  ]
    .map(kind => ({ ...kind, journals: kind.journals.filter(code => codes.includes(code)) }))
    .filter(kind => kind.journals.length > 0);
}

/** Moves a reward already counted by `pending_creator_reward` from pending to the creator. */
const DEPOSIT_JOURNAL = "creator_reward_deposit";

/**
 * Journals by credits moved, or with `signed`, by which way they moved player balances: into them
 * above zero, out of them below. Journals that never touch player balances are left out then.
 */
export function Journals({
  range,
  back,
  journals,
  signed,
  onChange,
}: {
  range: LedgerReportRange;
  back: number;
  journals: readonly string[] | undefined;
  signed: boolean;
  onChange: (next: {
    range?: LedgerReportRange;
    back?: number;
    journal?: string[];
    signed?: true | undefined;
  }) => void;
}) {
  return (
    <ReportFrame
      name="journalTotals"
      range={range}
      back={back}
      onPeriodChange={onChange}
      filters={() => (
        <DirectionSwitch
          signed={signed}
          labels={{ plain: "Moved", signed: "In and out" }}
          onChange={next => onChange({ signed: next })}
        />
      )}
    >
      {report => {
        const kinds = movedJournalKinds(report.totals.filter(total => total.entryCount > 0));
        if (kinds.length === 0) return { chart: <Note>No activity in this range.</Note> };
        const totalsByCode = new Map(report.totals.map(total => [total.journalCode, total]));
        const ordered = kinds.flatMap(kind =>
          kind.journals.flatMap(code => totalsByCode.get(code) ?? []),
        );
        const codes = ordered.map(total => total.journalCode);
        const pick = usualPick(
          journals,
          codes,
          codes.filter(code => code !== DEPOSIT_JOURNAL),
        );
        const picked = largestFirst(
          ordered.filter(total => pick.values.includes(total.journalCode)),
          total => BigInt(total.movedTotalRaw),
        );
        const direction = new Map(
          kinds.flatMap(kind => kind.journals.map(code => [code, kind.direction] as const)),
        );
        const directionOf = (code: string) => (signed ? (direction.get(code) ?? 0) : 1);
        const rows = picked.filter(total => directionOf(total.journalCode) !== 0);
        const amountRaw = (total: JournalTotal) =>
          (BigInt(total.movedTotalRaw) * BigInt(directionOf(total.journalCode))).toString();
        const amounts = rows.map(amountRaw);
        const kindSubtotals = kinds.flatMap(kind => {
          const members = rows.filter(total => kind.journals.includes(total.journalCode));
          return members.length === 0 ? [] : [{ ...kind, raw: sumRaw(members.map(amountRaw)) }];
        });
        const bars = chartBuckets(
          report.buckets,
          rows.map(total => total.journalCode),
          row => row.journalCode,
          row =>
            creditsFromRaw(row?.movedTotalRaw ?? "0") * (row ? directionOf(row.journalCode) : 1),
        );
        const bucketKindSubtotals = (index: number) =>
          kindSubtotals
            .map(kind => {
              const values = bars[index]?.values ?? [];
              const subtotal = rows.reduce(
                (sum, total, row) =>
                  kind.journals.includes(total.journalCode) ? sum + (values[row] ?? 0) : sum,
                0,
              );
              return `${kind.label} ${formatChartCredits(subtotal)}`;
            })
            .join(" · ");
        const journalName = journalReportLabel;
        return {
          tags: (
            <Tags
              label="Journals"
              options={ordered.map(total => ({
                value: total.journalCode,
                label: journalReportLabel(total.journalCode),
              }))}
              sections={kinds.map(kind => ({
                key: kind.key,
                label: kind.label,
                members: kind.journals,
              }))}
              usual={pick.usual}
              values={pick.values}
              onChange={codes => onChange({ journal: pick.keep(codes) })}
            />
          ),
          chart:
            rows.length === 0 ? (
              <Note>None of the picked journals moved in this range.</Note>
            ) : (
              <>
                <StackedBars
                  title={
                    signed
                      ? "Credits into and out of player balances, one direction per journal"
                      : "Credits moved per journal"
                  }
                  series={rows.map(total => ({
                    key: total.journalCode,
                    label: journalReportLabel(total.journalCode),
                  }))}
                  buckets={bars}
                  step={report.step}
                  bucketSummary={signed ? undefined : bucketKindSubtotals}
                  summary={
                    signed ? (
                      <>
                        <Credits signed raw={sumRaw(amounts)} /> net into player balances{" "}
                        {reportRangePhrase(range, back)} ·{" "}
                        <Credits raw={sumRaw(amounts.filter(raw => !raw.startsWith("-")))} /> in ·{" "}
                        <Credits
                          raw={sumRaw(amounts.filter(raw => raw.startsWith("-"))).replace("-", "")}
                        />{" "}
                        out
                      </>
                    ) : (
                      <>
                        {kindSubtotals.map((kind, index) => (
                          <span key={kind.key}>
                            {index > 0 && " · "}
                            {kind.label} <Credits raw={kind.raw} />
                          </span>
                        ))}{" "}
                        {reportRangePhrase(range, back)}
                      </>
                    )
                  }
                  format={formatChartCredits}
                />
                <Table
                  columns={[
                    { label: "Journal" },
                    { label: "Entries", numeric: true },
                    { label: signed ? "Into balances" : "Moved", numeric: true },
                  ]}
                  rows={[
                    ...rows.map(total => ({
                      key: total.journalCode,
                      cells: [
                        journalName(total.journalCode),
                        total.entryCount.toLocaleString(),
                        <Credits key="amount" signed={signed} raw={amountRaw(total)} />,
                      ],
                    })),
                    ...(signed
                      ? [
                          {
                            key: "net",
                            cells: [
                              <span key="label" className="font-medium">
                                Net
                              </span>,
                              rows
                                .reduce((count, total) => count + total.entryCount, 0)
                                .toLocaleString(),
                              <Credits key="net" signed toned raw={sumRaw(amounts)} />,
                            ],
                          },
                        ]
                      : []),
                  ]}
                />
              </>
            ),
        };
      }}
    </ReportFrame>
  );
}
